import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getSeoForPath, type SeoRouteConfig } from './routes';
import { toAbsoluteUrl } from './siteConfig';

type MetaAttr = 'name' | 'property';

const META_NAME_KEYS = [
  'description',
  'keywords',
  'robots',
  'twitter:card',
  'twitter:title',
  'twitter:description',
  'twitter:image',
  'twitter:image:alt',
];

const META_PROPERTY_KEYS = [
  'og:title',
  'og:description',
  'og:image',
  'og:image:alt',
  'og:url',
  'og:type',
  'og:site_name',
  'og:locale',
];

interface HeadSnapshot {
  title: string;
  names: Record<string, string | null>;
  properties: Record<string, string | null>;
  canonical: string | null;
}

const readMeta = (attr: MetaAttr, key: string): string | null => {
  const el = document.head.querySelector(`meta[${attr}="${key}"]`);
  return el ? el.getAttribute('content') : null;
};

const setMeta = (attr: MetaAttr, key: string, content: string | null): void => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (content === null || content === '') {
    if (el && content === null) {
      el.remove();
    }
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const readCanonical = (): string | null => {
  const el = document.head.querySelector('link[rel="canonical"]');
  return el ? el.getAttribute('href') : null;
};

const setCanonical = (href: string | null): void => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (href === null) {
    if (el) {
      el.remove();
    }
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const captureSnapshot = (): HeadSnapshot => {
  const names: Record<string, string | null> = {};
  const properties: Record<string, string | null> = {};
  META_NAME_KEYS.forEach((key) => {
    names[key] = readMeta('name', key);
  });
  META_PROPERTY_KEYS.forEach((key) => {
    properties[key] = readMeta('property', key);
  });
  return {
    title: document.title,
    names,
    properties,
    canonical: readCanonical(),
  };
};

const restoreSnapshot = (snapshot: HeadSnapshot): void => {
  document.title = snapshot.title;
  META_NAME_KEYS.forEach((key) => setMeta('name', key, snapshot.names[key]));
  META_PROPERTY_KEYS.forEach((key) =>
    setMeta('property', key, snapshot.properties[key])
  );
  setCanonical(snapshot.canonical);
};

const applySeo = (seo: SeoRouteConfig): void => {
  document.title = seo.title;

  setMeta('name', 'description', seo.description);
  setMeta('name', 'keywords', seo.keywords);
  setMeta('name', 'robots', seo.robots);

  setCanonical(seo.canonicalPath ? toAbsoluteUrl(seo.canonicalPath) : null);

  const url = seo.canonicalPath ? toAbsoluteUrl(seo.canonicalPath) : '';
  setMeta('property', 'og:title', seo.title);
  setMeta('property', 'og:description', seo.description);
  setMeta('property', 'og:image', seo.image);
  setMeta('property', 'og:image:alt', seo.title);
  setMeta('property', 'og:url', url);
  setMeta('property', 'og:type', 'website');

  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:title', seo.title);
  setMeta('name', 'twitter:description', seo.description);
  setMeta('name', 'twitter:image', seo.image);
  setMeta('name', 'twitter:image:alt', seo.title);
};

/**
 * Single centralized, route-aware head manager.
 * Keeps exactly one managed tag per name/property/rel and replaces or restores
 * every managed value on each route change so nothing leaks between routes.
 */
export default function SeoManager() {
  const { pathname } = useLocation();
  const snapshotRef = useRef<HeadSnapshot | null>(null);

  if (snapshotRef.current === null && typeof document !== 'undefined') {
    snapshotRef.current = captureSnapshot();
  }

  useEffect(() => {
    const seo = getSeoForPath(pathname);
    if (seo.inheritHead) {
      if (snapshotRef.current) {
        restoreSnapshot(snapshotRef.current);
      }
      return;
    }
    applySeo(seo);
  }, [pathname]);

  return null;
}